import { useWindowWidth } from '@react-hook/window-size';
import { FC, useMemo } from 'react';
import { Configure, Index } from 'react-instantsearch';

import { ProductResults } from './results/Products';
import { ArticleResults } from './results/Articles';
import { CollectionResults } from './results/Collections';
import { DangerousHTML } from '../../shared/components/DangerousHTML';
import { HideWhenSearchFailed, SearchUnavailable } from '../../shared/components';
import { useSearchFailed } from '../../shared/use/search-failed';
import { AlgoliaUtil } from '../../shared/utils';

const productAttributesToRetrieve = [
  'title',
  'sku',
  'MPN',
  'handle',
  'availability',
  'price',
  'image',
  'product_image',
  'objectID',
  'ShopifyObjectID',
];

type SearchResultsDropdownProps = {
  product: string;
  collection: string;
  article: string;
  algoliaUtil: AlgoliaUtil;
};

export const SearchResultsDropdown: FC<SearchResultsDropdownProps> = ({
  product,
  collection,
  article,
  algoliaUtil,
}) => {
  const windowWidth = useWindowWidth();
  const isMobile = windowWidth < 600;
  const searchFailed = useSearchFailed();

  const dropdownMenuClass = useMemo(() => {
    const classes = [
      'aa-dropdown-menu',
      'aa-without-articles',
      'aa-with-products',
      'aa-with-pages',
      'aa-with-collections',
      'aa-without-footer',
    ];

    if (windowWidth < 320) {
      classes.push('aa-dropdown-menu-size-xs');
    } else if (windowWidth < 600) {
      classes.push('aa-dropdown-menu-size-sm');
    } else {
      classes.push('aa-dropdown-menu-size-lg');
    }

    return classes.join(' ');
  }, [windowWidth]);

  const products = (
    <div className="aa-products">
      <DangerousHTML className="aa-products-header" content={theme.locales.autocomplete.titles.products} />
      <div className="aa-dataset-products">
        <Index indexName={product}>
          <Configure
            hitsPerPage={8}
            attributesToRetrieve={productAttributesToRetrieve}
            userToken={algoliaUtil.userToken}
          />
          <ProductResults algoliaUtil={algoliaUtil} />
        </Index>
      </div>
    </div>
  );

  const collectionTypeFacetFilter = 'collection_type';

  return (
    <div className={dropdownMenuClass}>
      {/* `status` is per InstantSearch instance, not per index, so the message belongs here once
          rather than inside each index's results. */}
      {searchFailed ? <SearchUnavailable className="aa-search-unavailable" /> : null}

      <HideWhenSearchFailed>
        <div className="aa-columns-container">
          <div className="aa-left-column">
            <div className="aa-collections">
              <DangerousHTML
                className="aa-collections-header"
                content={theme.locales.autocomplete.titles.collections}
              />
              <div className="aa-dataset-collections">
                <Index indexName={collection}>
                  <Configure
                    hitsPerPage={4}
                    attributesToRetrieve={['title', 'handle']}
                    facetFilters={['template_suffix:-virtual', `${collectionTypeFacetFilter}:category`]}
                    userToken={algoliaUtil.userToken}
                  />
                  <CollectionResults algoliaUtil={algoliaUtil} isBrands={false} />
                </Index>
              </div>
            </div>
            <div className="aa-collections">
              <DangerousHTML className="aa-collections-header" content={theme.locales.autocomplete.titles.brands} />
              <div className="aa-dataset-collections">
                <Index indexName={collection}>
                  <Configure
                    hitsPerPage={4}
                    attributesToRetrieve={['title', 'handle']}
                    facetFilters={['template_suffix:-virtual', `${collectionTypeFacetFilter}:brand`]}
                    userToken={algoliaUtil.userToken}
                  />
                  <CollectionResults algoliaUtil={algoliaUtil} isBrands />
                </Index>
              </div>
            </div>
            {isMobile && products}
            <div className="aa-pages">
              <DangerousHTML className="aa-pages-header" content={theme.locales.autocomplete.titles.articles} />
              <div className="aa-dataset-pages">
                <Index indexName={article}>
                  <Configure
                    hitsPerPage={4}
                    attributesToRetrieve={['title', 'handle']}
                    userToken={algoliaUtil.userToken}
                  />
                  <ArticleResults algoliaUtil={algoliaUtil} />
                </Index>
              </div>
            </div>
          </div>
          {!isMobile && <div className="aa-right-column aa-right-column-lg">{products}</div>}
        </div>
      </HideWhenSearchFailed>
    </div>
  );
};
